import Link from 'next/link'
import { Chip } from '@/components/parts/chip'
import { ShareLinks } from '@/components/parts/share-links'
import { Time } from '@/components/parts/time'
import { DEFAULT_DOWNLOAD_HREF } from '@/constants/links'

type RailSidebarBanner = {
  href: string
  src: string
  srcSet?: string
  width: number
  height: number
  alt: string
}

type RailSidebarCategory = {
  label: string
  href: string
  count: number
  /** 表示中のカテゴリー（カテゴリー別一覧で太字にする） */
  current?: boolean
}

/** 新着記事の 1 件。date は microCMS の日時（ISO 8601） */
export type RailSidebarArticle = {
  href: string
  title: string
  image?: { src: string }
  tags?: string[]
  date: string
  author: string
}

type RailSidebarSeminar = {
  href: string
  title: string
  image?: { src: string; srcSet?: string; sizes?: string }
}

type RailSidebarProps = {
  banners?: RailSidebarBanner[]
  categories?: RailSidebarCategory[]
  /** 記事ページで出す新着記事（Figma: col/新着コラム） */
  articles?: RailSidebarArticle[]
  seminar?: RailSidebarSeminar
  /** SNS 共有に使うページの URL とタイトル */
  share?: { url: string; title: string }
}

/**
 * コラムの右カラムに置くバナー。
 * Figma の枠（420×166）はプレースホルダーのため、現行の資料請求・ライブデモのバナーを使う
 */
export const COLUMN_BANNERS: RailSidebarBanner[] = [
  {
    href: DEFAULT_DOWNLOAD_HREF,
    src: '/images/banner-column.webp',
    srcSet: '/images/banner-column.webp 1x, /images/banner-column@2x.webp 2x',
    width: 358,
    height: 160,
    alt: '資料請求ダウンロード'
  },
  {
    href: '/demo/',
    src: '/images/banner-column02.webp',
    srcSet:
      '/images/banner-column02.webp 1x, /images/banner-column02@2x.webp 2x',
    width: 342,
    height: 152,
    alt: '今すぐライブデモを試す'
  }
]

function RailHeading({ children }: { children: string }) {
  return (
    <h2 className="fontfeaturesettings text-2xl font-bold tracking-heading text-ink">
      {children}
    </h2>
  )
}

/**
 * 一覧・記事ページの右カラム。Figma: rail（Type=sidebar）。
 * バナーの下に、生成りのカード 1 枚でカテゴリー件数・新着記事・セミナー情報・SNS 共有を並べる。
 */
export function RailSidebar({
  banners = [],
  categories = [],
  articles = [],
  seminar,
  share
}: RailSidebarProps) {
  const hasCard =
    categories.length > 0 || articles.length > 0 || seminar || share

  return (
    <div className="flex w-full flex-col gap-8">
      {banners.length > 0 ? (
        <ul className="flex flex-col gap-4">
          {banners.map((banner) => (
            <li key={banner.href}>
              <Link
                href={banner.href}
                className="block transition-opacity hover:opacity-80"
              >
                <img
                  src={banner.src}
                  srcSet={banner.srcSet}
                  width={banner.width}
                  height={banner.height}
                  alt={banner.alt}
                  loading="lazy"
                  className="h-auto w-full"
                />
              </Link>
            </li>
          ))}
        </ul>
      ) : null}

      {hasCard ? (
        <div className="flex flex-col gap-8 rounded-xl bg-surface-subtle p-6 lg:p-8">
          {categories.length > 0 ? (
            <section className="flex flex-col gap-5">
              <RailHeading>カテゴリー</RailHeading>
              <ul className="flex flex-col gap-3">
                {categories.map((category, index) => (
                  <li key={category.href} className="flex flex-col gap-3">
                    {index > 0 ? (
                      <span aria-hidden="true" className="divider-dashed-x" />
                    ) : null}
                    <Link
                      href={category.href}
                      aria-current={category.current ? 'page' : undefined}
                      className="group/category flex items-center gap-2 text-base leading-body text-ink"
                    >
                      <span
                        className={`min-w-0 flex-1 underline-offset-4 group-hover/category:underline ${
                          category.current ? 'font-bold' : ''
                        }`}
                      >
                        {category.label}
                      </span>
                      <span className="inline-flex h-6 shrink-0 items-center justify-center rounded-sm border border-line-subtle bg-surface px-2 text-sm leading-compact">
                        {category.count}
                        <span className="sr-only">件</span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {articles.length > 0 ? (
            <section className="flex flex-col gap-5">
              <RailHeading>新着コラム</RailHeading>
              <ul className="flex flex-col gap-3">
                {articles.map((article, index) => (
                  <li key={article.href} className="flex flex-col gap-3">
                    {index > 0 ? (
                      <span aria-hidden="true" className="divider-dashed-x" />
                    ) : null}
                    <Link
                      href={article.href}
                      className="group/article flex flex-col gap-3 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-line-inverse"
                    >
                      <span className="flex items-center gap-3">
                        <span className="h-17 w-30 shrink-0 overflow-hidden rounded-xl bg-surface-muted">
                          {article.image?.src ? (
                            <img
                              src={article.image.src}
                              alt=""
                              width={120}
                              height={68}
                              loading="lazy"
                              className="size-full object-cover"
                            />
                          ) : null}
                        </span>
                        <span className="line-clamp-2 min-w-0 flex-1 text-sm font-bold leading-compact text-ink underline-offset-4 group-hover/article:underline">
                          {article.title}
                        </span>
                      </span>
                      <span className="flex flex-wrap items-center gap-2 text-sm leading-compact text-ink">
                        {article.tags?.map((tag) => (
                          <Chip key={tag} label={tag} surface="white" />
                        ))}
                        <span className="flex items-center gap-1">
                          <span
                            aria-hidden="true"
                            className="icon icon-calendar size-6"
                          />
                          <Time gmt={article.date} />
                        </span>
                        <span className="flex items-center gap-1">
                          <span
                            aria-hidden="true"
                            className="icon icon-user size-6"
                          />
                          <span className="sr-only">著者</span>
                          {article.author}
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {seminar ? (
            <section className="flex flex-col gap-5">
              <RailHeading>セミナー情報</RailHeading>
              <Link
                href={seminar.href}
                className="group/seminar block overflow-hidden rounded-xl bg-surface-muted focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-line-inverse"
              >
                {seminar.image?.src ? (
                  <img
                    src={seminar.image.src}
                    srcSet={seminar.image.srcSet}
                    sizes={seminar.image.sizes}
                    alt={seminar.title}
                    loading="lazy"
                    className="aspect-banner w-full object-cover transition-transform duration-500 ease-in-out will-change-transform group-hover/seminar:scale-105"
                  />
                ) : (
                  <span className="block p-5 text-base font-bold leading-compact text-ink">
                    {seminar.title}
                  </span>
                )}
              </Link>
            </section>
          ) : null}

          {share ? <ShareLinks url={share.url} title={share.title} /> : null}
        </div>
      ) : null}
    </div>
  )
}
